import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import ShowInSelect, { EVENT_CURRENCY, FxFootnote, FxMissingAlert, readFxOptions } from '../ShowInSelect';
import submitExport, { selectionFields } from '../submitExport';
import SelectedProjectsStrip from '../summary/filter/SelectedProjectsStrip';
import ProjectPicker from '../summary/picker/ProjectPicker';

import BudgetOverviewFilters from './BudgetOverviewFilters';
import { columnDefinitions } from './budgetOverviewFormat';
import BudgetOverviewKpis from './BudgetOverviewKpis';
import BudgetOverviewTable from './BudgetOverviewTable';
import useBudgetOverviewFilters from './useBudgetOverviewFilters';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import CheckboxInput from 'CommonComponents/CheckboxInput';

import { ConfirmationModalProvider } from 'Context/ConfirmationModalContext';

import FileExportIcon from 'Assets/icons/file-export.svg';

const DEFINITION_ORDER = ['budget', 'markups', 'total', 'margin', 'revenue', 'committed', 'actuals', 'remaining'];

/** A state updater that adds the id to a set, or takes it out if present. */
const toggleMember = (id) => (previous) => {
    const next = new Set(previous);
    if (next.has(id)) {
        next.delete(id);
    } else {
        next.add(id);
    }

    return next;
};

export default function ProjectsBudgetOverviewIndex({ organisationId, dataUrl, exportUrl, eventsUrl, savedSetsUrl, isOrganisationAdmin, fxOptions }) {
    const [events, setEvents] = useState([]);
    const [withArchived, setWithArchived] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);

    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [expandedProjects, setExpandedProjects] = useState(() => new Set());
    const [expandedDepartments, setExpandedDepartments] = useState(() => new Set());
    const [showIn, setShowIn] = useState(EVENT_CURRENCY);
    const fxAvailable = !!fxOptions?.anchor;

    const { hydrated, filterData, status, committedActuals, selectedEvents, selectedCount, removeProject, setSelection, setStatus, setCommittedActuals } =
        useBudgetOverviewFilters(organisationId, events);

    const readSeq = useRef(0);

    useEffect(() => {
        const url = new URL(eventsUrl);
        if (withArchived) {
            url.searchParams.set('with_archived', '1');
        }

        axios
            .get(url.toString())
            .then(({ data }) => setEvents(data.events || []))
            .catch(() => setError('Something went wrong loading the projects.'));
    }, [eventsUrl, withArchived]);

    useEffect(() => {
        if (!hydrated) {
            return;
        }

        if (selectedCount === 0) {
            readSeq.current++;
            setReport(null);
            setLoading(false);

            return;
        }

        const sequence = ++readSeq.current;
        setLoading(true);
        setError(null);

        axios
            .post(dataUrl, { filters: filterData, status, show_in: showIn })
            .then(({ data }) => {
                // Only the newest read may land: the controls stay live while one
                // is in flight.
                if (sequence === readSeq.current) {
                    setReport(data);
                }
            })
            .catch(() => {
                if (sequence === readSeq.current) {
                    setError('Something went wrong loading the report.');
                }
            })
            .finally(() => {
                if (sequence === readSeq.current) {
                    setLoading(false);
                }
            });
    }, [hydrated, dataUrl, filterData, status, selectedCount, showIn]);

    const definitions = useMemo(() => columnDefinitions(report?.margin_basis), [report?.margin_basis]);

    const columns = useMemo(() => ({ revenue: !!report?.show_revenue, committedActuals }), [report?.show_revenue, committedActuals]);

    // Totals and cards format in the currency's locale only when every project
    // in view shares it; mixed totals fall back to the page's conventions.
    // A project left in its own currency carries its own locale, not the totals'.
    const locale = !report || report.mixed_currency ? null : report.projects.find((project) => project.locale && !project.fx?.missing)?.locale || null;
    const fx = report?.fx || null;

    const expandableProjectIds = useMemo(
        () => (report?.projects || []).filter((project) => project.has_budget && project.departments.length > 0).map((project) => project.id),
        [report],
    );

    const allExpanded = expandableProjectIds.length > 0 && expandableProjectIds.every((id) => expandedProjects.has(id));

    const toggleProject = useCallback((id) => setExpandedProjects(toggleMember(id)), []);
    const toggleDepartment = useCallback((id) => setExpandedDepartments(toggleMember(id)), []);

    const expandAll = (checked) => setExpandedProjects(checked ? new Set(expandableProjectIds) : new Set());

    const exportReport = () => {
        const fields = [['status', status], ['committed_actuals', committedActuals ? '1' : '0'], ['show_in', showIn], ...selectionFields(filterData)];

        expandableProjectIds.filter((id) => expandedProjects.has(id)).forEach((id) => fields.push(['expanded_projects[]', id]));
        expandedDepartments.forEach((id) => fields.push(['expanded_departments[]', id]));

        submitExport(exportUrl, fields);
    };

    const noBudgetCount = report?.no_budget_count || 0;

    return (
        <div className="tw-space-y-4">
            <div className="card bg-white border-0 rounded-15px shadow">
                <div className="card-body tw-space-y-4">
                    <div>
                        <h3 className="table__header tw-mb-1">Budget, mark-ups and margin across your projects</h3>
                        <p className="tw-text-sm tw-text-charcoal-50 tw-mb-0">
                            Total budgets, mark-ups and margins for all or selected projects, by department and cost centre. Budget version is set per project.
                        </p>
                    </div>

                    <BudgetOverviewFilters
                        events={events}
                        selectedCount={selectedCount}
                        status={status}
                        committedActuals={committedActuals}
                        onOpenPicker={() => setPickerOpen(true)}
                        onChangeStatus={setStatus}
                        onChangeCommittedActuals={setCommittedActuals}
                    />

                    {pickerOpen && (
                        <ProjectPicker
                            events={events}
                            filterData={filterData}
                            isAdmin={!!isOrganisationAdmin}
                            savedSetsUrl={savedSetsUrl}
                            showArchived={withArchived}
                            onToggleArchived={setWithArchived}
                            onApply={(draft) => {
                                setSelection(draft);
                                setPickerOpen(false);
                            }}
                            onClose={() => setPickerOpen(false)}
                        />
                    )}

                    {/* Without an FX anchor the page is exactly as it was: no Show in. */}
                    {fxAvailable ? (
                        <div className="tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-4">
                            <div className="tw-min-w-0 tw-flex-1">
                                <SelectedProjectsStrip selectedEvents={selectedEvents} onRemove={removeProject} showCurrency />
                            </div>
                            <ShowInSelect fxOptions={fxOptions} value={showIn} onChange={setShowIn} />
                        </div>
                    ) : (
                        <SelectedProjectsStrip selectedEvents={selectedEvents} onRemove={removeProject} showCurrency />
                    )}

                    {report?.mixed_currency && (
                        <Alert type="warning">Selected projects use more than one currency. Totals are shown without currency conversion.</Alert>
                    )}
                </div>
            </div>

            {error && <Alert type="error">{error}</Alert>}

            <FxMissingAlert fx={fx} fxUrl={fxOptions?.fx_url} />

            {hydrated && selectedCount === 0 ? (
                <div className="tw-px-4 tw-py-8 tw-text-center tw-text-sm tw-text-charcoal-50 tw-rounded-lg tw-border-2 tw-border-dashed tw-border-grey">
                    Select projects to begin
                </div>
            ) : (
                <>
                    <BudgetOverviewKpis report={report} definitions={definitions} committedActuals={committedActuals} locale={locale} />

                    <div className="table__card tw-space-y-4">
                        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4">
                            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-6 tw-gap-y-2">
                                <h3 className="table__header tw-mb-0">{$t('By project')}</h3>
                                <div className="tw-text-sm tw-text-charcoal">
                                    <CheckboxInput
                                        label={$t('Expand departments')}
                                        checked={allExpanded}
                                        disabled={expandableProjectIds.length === 0}
                                        onChange={(event) => expandAll(event.target.checked)}
                                    />
                                </div>
                            </div>
                            <Button variant="primary" onClick={exportReport} disabled={!report}>
                                <FileExportIcon className="tw-w-3 tw-h-3" />
                                Export to excel
                            </Button>
                        </div>

                        {loading && !report && <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">Loading…</p>}

                        {report && (
                            <BudgetOverviewTable
                                report={report}
                                fx={fx}
                                definitions={definitions}
                                columns={columns}
                                locale={locale}
                                expandedProjects={expandedProjects}
                                expandedDepartments={expandedDepartments}
                                onToggleProject={toggleProject}
                                onToggleDepartment={toggleDepartment}
                            />
                        )}

                        {report && fxAvailable && <FxFootnote fx={fx} />}

                        {noBudgetCount > 0 && (
                            <p className="tw-text-xs tw-text-charcoal-50 tw-mb-0">
                                {noBudgetCount === 1 ? '1 selected project has no budget.' : `${noBudgetCount} selected projects have no budget.`}
                            </p>
                        )}

                        <div className="tw-border-t tw-border-grey tw-pt-4">
                            <h4 className="tw-text-base tw-font-semibold tw-text-charcoal tw-mb-3">{$t('How these figures are calculated')}</h4>
                            <dl className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-x-6 tw-gap-y-4 tw-mb-0">
                                {DEFINITION_ORDER.map((key) => (
                                    <div key={key}>
                                        <dt className="tw-text-sm tw-font-medium tw-text-charcoal">{definitions[key].label}</dt>
                                        <dd className="tw-text-sm tw-text-charcoal-50 tw-mb-0">{definitions[key].text}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

if (document.getElementById('ProjectsBudgetOverview')) {
    const rootEl = document.getElementById('ProjectsBudgetOverview');

    ReactDOM.render(
        <App>
            <ConfirmationModalProvider>
                <ProjectsBudgetOverviewIndex
                    organisationId={rootEl.getAttribute('data-organisation-id')}
                    dataUrl={rootEl.getAttribute('data-data-url')}
                    exportUrl={rootEl.getAttribute('data-export-url')}
                    eventsUrl={rootEl.getAttribute('data-events-url')}
                    savedSetsUrl={rootEl.getAttribute('data-saved-sets-url')}
                    isOrganisationAdmin={rootEl.getAttribute('data-is-organisation-admin') === '1'}
                    fxOptions={readFxOptions(rootEl)}
                />
            </ConfirmationModalProvider>
        </App>,
        rootEl,
    );
}
